import { createTakeFile, deleteTake, listTakes, readTakeFile, saveTake } from "./store"
import { fetchStatus, uploadTake } from "./upload"

const VIDEO_BITS_PER_SECOND = 1_500_000
const AUDIO_BITS_PER_SECOND = 96_000
const WARN_BYTES = 350 * 1024 * 1024
const STOP_BYTES = 450 * 1024 * 1024

export class RecorderApp {
  constructor(element, { csrf, email }) {
    this.element = element
    this.csrf = csrf
    this.email = email
    this.mode = "idle"
    this.bytes = 0
    this.elapsedMs = 0
    this.takes = []
    this.uploading = false
    this.warned = false
    this.onBeforeUnload = (event) => {
      if (this.mode === "recording" || this.takes.some((take) => take.phase !== "done")) {
        event.preventDefault()
        event.returnValue = ""
      }
    }
    this.onPageHide = () => {
      if (this.recorder && this.recorder.state !== "inactive") {
        this.mode = "finish"
        this.recorder.stop()
      }
    }
  }

  async start() {
    window.addEventListener("beforeunload", this.onBeforeUnload)
    window.addEventListener("pagehide", this.onPageHide)
    if (this.cameraInput.checked) await this.enablePreview()
    await this.refreshQueue()
    this.pumpQueue()
  }

  destroy() {
    this.closed = true
    window.removeEventListener("beforeunload", this.onBeforeUnload)
    window.removeEventListener("pagehide", this.onPageHide)
    this.stopDrawing()
    this.preview?.pause()
  }

  async record() {
    if (this.mode === "recording") return
    this.startButton.disabled = true
    this.setMessage("Choose what to share. Recording starts after a short countdown.")

    let pipWindow
    try {
      if (!("documentPictureInPicture" in window)) {
        throw new Error("This recorder needs Chrome so the controls can stay on top while you record.")
      }

      const pipPromise = documentPictureInPicture.requestWindow({ width: 380, height: 620 })
      const displayPromise = navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: false,
        selfBrowserSurface: "exclude",
        monitorTypeSurfaces: "include",
        surfaceSwitching: "include"
      })
      const micPromise = this.micInput.checked
        ? navigator.mediaDevices.getUserMedia({ audio: true })
        : Promise.resolve(null)
      const cameraPromise = this.cameraInput.checked
        ? (this.cameraStream ? Promise.resolve(this.cameraStream) : navigator.mediaDevices.getUserMedia({ video: true }))
        : Promise.resolve(null)

      const [pip, displayStream, micStream, cameraStream] = await Promise.all([
        pipPromise,
        displayPromise,
        micPromise,
        cameraPromise
      ])

      pipWindow = pip
      this.pipWindow = pipWindow
      this.displayStream = displayStream
      this.micStream = micStream
      this.cameraStream = cameraStream
      this.cameraOn = Boolean(cameraStream)
      this.muted = false
      displayStream.getVideoTracks()[0].addEventListener("ended", () => {
        if (this.mode === "recording") this.finish()
      })

      this.buildControls(pipWindow)
      await this.attachSources(pipWindow)
      this.startDrawing(pipWindow)
      await this.countdown(pipWindow)
      await this.beginTake()
    } catch (error) {
      this.stopStreams()
      pipWindow?.close()
      this.pipWindow = null
      this.startButton.disabled = false
      this.setMessage(this.friendlyError(error))
    }
  }

  async beginTake() {
    this.localId = crypto.randomUUID()
    this.bytes = 0
    this.warned = false
    this.accumulatedMs = 0
    this.startedAt = performance.now()
    this.mode = "recording"
    this.writable = await createTakeFile(this.localId)
    this.writeChain = Promise.resolve()
    await saveTake({
      localId: this.localId,
      phase: "recording",
      title: this.currentTitle(),
      createdAt: Date.now(),
      byteSize: 0,
      durationMs: 0,
      publicId: null,
      watchUrl: null,
      error: null
    })

    const tracks = [ ...this.canvasStream.getVideoTracks() ]
    if (this.audioDestination) tracks.push(this.audioDestination.stream.getAudioTracks()[0])
    const mimeType = this.mimeType()
    if (!mimeType) throw new Error("This browser cannot record a WebM video.")
    this.recorder = new MediaRecorder(new MediaStream(tracks), {
      mimeType,
      videoBitsPerSecond: VIDEO_BITS_PER_SECOND,
      audioBitsPerSecond: AUDIO_BITS_PER_SECOND
    })
    this.recorder.ondataavailable = (event) => {
      if (!event.data || event.data.size === 0) return
      this.writeChain = this.writeChain.then(async () => {
        await this.writable.write(event.data)
        this.bytes += event.data.size
        this.renderMeter()
        if (this.bytes >= STOP_BYTES && this.recorder?.state === "recording") this.finish("size")
      })
    }
    this.recorder.onstop = () => this.handleStop()
    this.recorder.start(1000)
    this.renderTimer()
    this.timer = window.setInterval(() => this.renderTimer(), 250)
    this.setMessage("Recording. Use the control window to pause or stop. You can switch away from this tab.")
  }

  async handleStop() {
    const localId = this.localId
    const discarding = this.mode === "discard"
    await this.writeChain
    const durationMs = this.currentElapsed()
    const byteSize = this.bytes
    await this.writable?.close().catch(() => {})
    this.writable = null
    window.clearInterval(this.timer)

    if (discarding) {
      await deleteTake(localId)
      this.recorder = null
      this.mode = "armed"
      return
    }

    await saveTake({
      localId,
      phase: "pending",
      title: this.currentTitle(),
      createdAt: Date.now(),
      byteSize,
      durationMs,
      publicId: null,
      watchUrl: null,
      error: null
    })
    this.stopStreams()
    this.stopDrawing()
    this.pipWindow?.close()
    this.pipWindow = null
    this.mode = "idle"
    this.startButton.disabled = false
    this.setMessage(byteSize >= STOP_BYTES
      ? "Recording stopped near 450 MB so the file can be accepted. Uploading now."
      : "Recording stopped. Uploading it now.")
    await this.refreshQueue()
    this.pumpQueue()
  }

  async finish(reason) {
    if (!this.recorder || this.recorder.state === "inactive" || this.mode === "finish") return
    this.mode = "finish"
    this.stopReason = reason
    this.recorder.stop()
  }

  async restart() {
    if (!this.recorder || this.recorder.state === "inactive" || !this.displayStream || this.mode === "discard") return
    this.mode = "discard"
    this.recorder.stop()
    await this.waitUntil(() => this.mode === "armed")
    this.bytes = 0
    this.warned = false
    this.accumulatedMs = 0
    await this.countdown(this.pipWindow)
    await this.beginTake()
  }

  togglePause() {
    if (!this.recorder) return
    if (this.recorder.state === "recording") {
      this.accumulatedMs = this.currentElapsed()
      this.recorder.pause()
      this.pauseButton.textContent = "Resume"
    } else if (this.recorder.state === "paused") {
      this.startedAt = performance.now()
      this.recorder.resume()
      this.pauseButton.textContent = "Pause"
    }
  }

  toggleMute() {
    if (!this.gain) return
    this.muted = !this.muted
    this.gain.gain.value = this.muted ? 0 : 1
    this.muteButton.textContent = this.muted ? "Unmute" : "Mute"
  }

  async toggleCamera() {
    this.cameraOn = !this.cameraOn
    if (this.cameraOn && !this.cameraStream) {
      try {
        this.cameraStream = await navigator.mediaDevices.getUserMedia({ video: true })
        this.cameraVideo.srcObject = this.cameraStream
        await this.cameraVideo.play()
      } catch (error) {
        this.cameraOn = false
        this.setMessage(this.friendlyError(error))
      }
    }
    if (!this.cameraOn && this.cameraStream) {
      this.cameraStream.getTracks().forEach((track) => track.stop())
      this.cameraStream = null
      if (this.cameraVideo) this.cameraVideo.srcObject = null
    }
    if (this.cameraButton) this.cameraButton.textContent = this.cameraOn ? "Camera on" : "Camera off"
    this.cameraInput.checked = this.cameraOn
  }

  async togglePreview() {
    if (this.mode === "recording") return
    if (this.cameraInput.checked) await this.enablePreview()
    else this.disablePreview()
  }

  async enablePreview() {
    if (this.cameraStream) return
    try {
      this.cameraStream = await navigator.mediaDevices.getUserMedia({ video: true })
      this.preview.srcObject = this.cameraStream
      await this.preview.play()
    } catch (error) {
      this.cameraInput.checked = false
      this.setMessage(this.friendlyError(error))
    }
  }

  disablePreview() {
    this.cameraStream?.getTracks().forEach((track) => track.stop())
    this.cameraStream = null
    this.preview.srcObject = null
  }

  buildControls(pipWindow) {
    const doc = pipWindow.document
    doc.head.innerHTML = `<style>
      body { margin: 0; font-family: Outfit, ui-sans-serif, system-ui, sans-serif; background: #011738; color: #f3f6fb; }
      #stage { position: relative; background: #000; }
      canvas { width: 100%; height: auto; display: block; }
      #countdown { position: absolute; inset: 0; display: none; align-items: center; justify-content: center; font-size: 96px; font-weight: 600; background: rgba(0,0,0,.35); }
      .panel { padding: 16px; }
      #timer { font-variant-numeric: tabular-nums; font-size: 28px; }
      #meter, #hint { color: #9aabc0; font-size: 12px; line-height: 1.4; }
      #warning { color: #f0c36a; font-size: 13px; min-height: 1.2em; }
      .row { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }
      button { border: 0; border-radius: 999px; padding: 8px 12px; background: #123056; color: #f3f6fb; }
      button.primary { background: #0271fd; }
      video { position: absolute; width: 2px; height: 2px; opacity: 0; }
    </style>`
    doc.body.innerHTML = `
      <div id="stage">
        <canvas id="canvas"></canvas>
        <div id="countdown"></div>
        <video id="display" autoplay muted playsinline></video>
        <video id="camera" autoplay muted playsinline></video>
      </div>
      <div class="panel">
        <div id="timer">00:00</div>
        <div id="meter">0 MB</div>
        <div id="warning"></div>
        <div class="row">
          <button id="pause" type="button">Pause</button>
          <button id="restart" type="button">Restart</button>
          <button id="stop" class="primary" type="button">Stop</button>
        </div>
        <div class="row">
          <button id="mute" type="button">Mute</button>
          <button id="camera-toggle" type="button">Camera on</button>
        </div>
        <p id="hint">Space pause · R restart · S stop · M mute · C camera. These work while this window is focused.</p>
      </div>`

    this.canvas = doc.getElementById("canvas")
    this.displayVideo = doc.getElementById("display")
    this.cameraVideo = doc.getElementById("camera")
    this.countdownEl = doc.getElementById("countdown")
    this.timerEl = doc.getElementById("timer")
    this.meterEl = doc.getElementById("meter")
    this.warningEl = doc.getElementById("warning")
    this.pauseButton = doc.getElementById("pause")
    this.muteButton = doc.getElementById("mute")
    this.cameraButton = doc.getElementById("camera-toggle")
    if (!this.micStream) this.muteButton.disabled = true

    this.pauseButton.addEventListener("click", () => this.togglePause())
    doc.getElementById("restart").addEventListener("click", () => this.restart())
    doc.getElementById("stop").addEventListener("click", () => this.finish())
    this.muteButton.addEventListener("click", () => this.toggleMute())
    this.cameraButton.addEventListener("click", () => this.toggleCamera())
    pipWindow.addEventListener("keydown", (event) => {
      if (event.code === "Space") {
        event.preventDefault()
        this.togglePause()
      } else if (event.key === "r" || event.key === "R") {
        this.restart()
      } else if (event.key === "s" || event.key === "S" || event.key === "Escape") {
        this.finish()
      } else if (event.key === "m" || event.key === "M") {
        this.toggleMute()
      } else if (event.key === "c" || event.key === "C") {
        this.toggleCamera()
      }
    })
    pipWindow.addEventListener("pagehide", () => {
      if (this.mode === "recording") this.finish()
    })
  }

  async attachSources(pipWindow) {
    this.displayVideo.srcObject = this.displayStream
    await this.displayVideo.play()
    if (this.displayVideo.readyState < 1) {
      await new Promise((resolve) => this.displayVideo.addEventListener("loadedmetadata", resolve, { once: true }))
    }

    if (this.cameraStream) {
      this.cameraVideo.srcObject = this.cameraStream
      await this.cameraVideo.play()
    }

    const [width, height] = fit(this.displayVideo.videoWidth || 1280, this.displayVideo.videoHeight || 720)
    this.canvas.width = width
    this.canvas.height = height
    this.canvasStream = this.canvas.captureStream(30)

    if (this.micStream) {
      this.audioContext = new AudioContext()
      await this.audioContext.resume()
      const source = this.audioContext.createMediaStreamSource(this.micStream)
      this.gain = this.audioContext.createGain()
      this.audioDestination = this.audioContext.createMediaStreamDestination()
      source.connect(this.gain)
      this.gain.connect(this.audioDestination)
    }

    pipWindow.document.getElementById("stage").dataset.ready = "true"
  }

  startDrawing(pipWindow) {
    const ctx = this.canvas.getContext("2d")
    const draw = () => {
      if (!this.canvas) return
      ctx.drawImage(this.displayVideo, 0, 0, this.canvas.width, this.canvas.height)
      if (this.cameraOn && this.cameraVideo?.readyState >= 2) drawCamera(ctx, this.cameraVideo, this.canvas.width, this.canvas.height)
      this.drawFrame = pipWindow.requestAnimationFrame(draw)
    }
    this.drawFrame = pipWindow.requestAnimationFrame(draw)
  }

  stopDrawing() {
    if (this.pipWindow && this.drawFrame) this.pipWindow.cancelAnimationFrame(this.drawFrame)
    this.drawFrame = null
  }

  async countdown(pipWindow) {
    for (const step of [3, 2, 1]) {
      this.countdownEl.style.display = "flex"
      this.countdownEl.textContent = String(step)
      await wait(1000)
    }
    this.countdownEl.style.display = "none"
    if (!pipWindow || pipWindow.closed) throw new Error("The control window closed before recording started.")
  }

  async refreshQueue() {
    this.takes = await listTakes()
    this.renderQueue()
  }

  renderQueue() {
    const visible = this.takes.filter((take) => take.phase !== "recording" && take.phase !== "done")
    this.queue.replaceChildren()
    if (visible.length === 0) {
      const empty = document.createElement("li")
      empty.textContent = "Finished takes show up here until your CRM has the file."
      this.queue.append(empty)
      return
    }

    visible.forEach((take) => {
      const item = document.createElement("li")
      item.className = "rounded-2xl bg-paper px-3 py-3 text-ink"
      const title = document.createElement("p")
      title.className = "font-medium"
      title.textContent = take.title
      const detail = document.createElement("p")
      detail.className = "mt-1 text-muted"
      detail.textContent = queueDetail(take)
      item.append(title, detail)
      if (take.watchUrl && take.phase === "awaiting_ghl") {
        const link = document.createElement("a")
        link.href = take.watchUrl
        link.className = "mt-2 inline-block text-ember"
        link.textContent = "Open watch page"
        item.append(link)
      }
      if (take.phase === "pending") {
        const button = document.createElement("button")
        button.type = "button"
        button.className = "mt-2 text-ember"
        button.textContent = "Upload now"
        button.addEventListener("click", () => this.pumpQueue())
        item.append(button)
      }
      this.queue.append(item)
    })
  }

  async pumpQueue() {
    if (this.uploading) return
    const next = (await listTakes()).find((take) => take.phase === "pending" || take.phase === "uploading")
    const waiting = (await listTakes()).filter((take) => take.phase === "awaiting_ghl")
    waiting.forEach((take) => this.pollTake(take))
    if (!next) return

    this.uploading = true
    try {
      await this.sendTake(next)
    } finally {
      this.uploading = false
      const more = (await listTakes()).some((take) => take.phase === "pending")
      if (more) this.pumpQueue()
    }
  }

  async sendTake(take) {
    take.phase = "uploading"
    take.error = null
    await saveTake(take)
    await this.refreshQueue()
    try {
      const file = await readTakeFile(take.localId)
      const result = await uploadTake({
        file,
        title: take.title,
        durationMs: take.durationMs,
        csrf: this.csrf,
        onProgress: () => {}
      })
      take.phase = "awaiting_ghl"
      take.publicId = result.public_id
      take.watchUrl = result.watch_url
      await saveTake(take)
      await this.refreshQueue()
      this.pollTake(take)
    } catch (error) {
      take.phase = "pending"
      take.error = error.message
      await saveTake(take)
      await this.refreshQueue()
      this.setMessage(error.message)
    }
  }

  async pollTake(take) {
    if (!take.publicId || this.polling?.has(take.localId)) return
    this.polling ||= new Set()
    this.polling.add(take.localId)
    try {
      let status = await fetchStatus(take.publicId)
      while (!this.closed && !status.ready && status.status !== "failed") {
        await wait(2000)
        status = await fetchStatus(take.publicId)
      }
      if (this.closed) return
      if (status.ready) {
        await deleteTake(take.localId)
        this.setMessage("The video is in the library.")
      } else if (status.status === "failed") {
        take.phase = "failed"
        take.error = status.error_message || "Your CRM did not accept the video. The copy is still on this computer."
        await saveTake(take)
        this.setMessage(take.error)
      }
      await this.refreshQueue()
    } catch (error) {
      this.setMessage(error.message)
    } finally {
      this.polling.delete(take.localId)
    }
  }

  renderTimer() {
    if (!this.timerEl) return
    this.timerEl.textContent = formatClock(this.currentElapsed())
  }

  renderMeter() {
    if (!this.meterEl) return
    const megabytes = this.bytes / (1024 * 1024)
    this.meterEl.textContent = `${megabytes.toFixed(0)} MB`
    if (!this.warned && this.bytes >= WARN_BYTES) {
      this.warned = true
      this.warningEl.textContent = "This take is getting large. Recording stops automatically near 450 MB."
    }
  }

  currentElapsed() {
    if (this.recorder?.state === "paused") return this.accumulatedMs
    if (!this.startedAt) return this.accumulatedMs || 0
    return this.accumulatedMs + (performance.now() - this.startedAt)
  }

  currentTitle() {
    const formatted = new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit"
    }).format(new Date())
    return `${formatted} · ${this.email}`
  }

  mimeType() {
    const candidates = [ "video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm" ]
    return candidates.find((type) => MediaRecorder.isTypeSupported(type)) || ""
  }

  stopStreams() {
    this.displayStream?.getTracks().forEach((track) => track.stop())
    this.micStream?.getTracks().forEach((track) => track.stop())
    this.canvasStream?.getTracks().forEach((track) => track.stop())
    this.audioContext?.close()
    this.displayStream = null
    this.micStream = null
    this.canvasStream = null
    this.audioContext = null
    this.gain = null
    this.audioDestination = null
    this.recorder = null
    if (!this.cameraInput.checked) this.disablePreview()
    else if (this.cameraStream && this.preview) {
      this.preview.srcObject = this.cameraStream
      this.preview.play().catch(() => {})
    }
  }

  friendlyError(error) {
    if (error?.name === "NotAllowedError") return "Permission was denied. Allow screen sharing, and the microphone or camera if you turned them on."
    if (error?.name === "AbortError") return "Screen sharing was canceled."
    return error?.message || "Recording could not start."
  }

  setMessage(text) {
    this.message.textContent = text
  }

  waitUntil(predicate) {
    return new Promise((resolve) => {
      const check = () => {
        if (predicate()) resolve()
        else window.setTimeout(check, 50)
      }
      check()
    })
  }

  get preview() { return this.element.querySelector("[data-recorder-target='preview']") }
  get message() { return this.element.querySelector("[data-recorder-target='message']") }
  get queue() { return this.element.querySelector("[data-recorder-target='queue']") }
  get startButton() { return this.element.querySelector("[data-recorder-target='start']") }
  get micInput() { return this.element.querySelector("[data-recorder-target='mic']") }
  get cameraInput() { return this.element.querySelector("[data-recorder-target='camera']") }
}

function fit(width, height) {
  const scale = Math.min(1, 1920 / width, 1080 / height)
  let w = Math.max(2, Math.round(width * scale))
  let h = Math.max(2, Math.round(height * scale))
  if (w % 2) w -= 1
  if (h % 2) h -= 1
  return [w, h]
}

function drawCamera(ctx, video, width, height) {
  const diameter = Math.round(width * 0.18)
  const margin = Math.round(width * 0.03)
  const x = width - diameter - margin
  const y = height - diameter - margin
  const vw = video.videoWidth || diameter
  const vh = video.videoHeight || diameter
  const scale = Math.max(diameter / vw, diameter / vh)
  const dw = vw * scale
  const dh = vh * scale
  ctx.save()
  ctx.beginPath()
  ctx.arc(x + diameter / 2, y + diameter / 2, diameter / 2, 0, Math.PI * 2)
  ctx.closePath()
  ctx.clip()
  ctx.drawImage(video, x + (diameter - dw) / 2, y + (diameter - dh) / 2, dw, dh)
  ctx.restore()
  ctx.beginPath()
  ctx.arc(x + diameter / 2, y + diameter / 2, diameter / 2 - 3, 0, Math.PI * 2)
  ctx.strokeStyle = "rgba(255,255,255,0.9)"
  ctx.lineWidth = Math.max(4, Math.round(width * 0.004))
  ctx.stroke()
}

function formatClock(ms) {
  const total = Math.max(0, Math.floor(ms / 1000))
  const minutes = Math.floor(total / 60)
  const seconds = total % 60
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
}

function queueDetail(take) {
  if (take.error) return take.error
  if (take.phase === "uploading") return "Uploading…"
  if (take.phase === "awaiting_ghl") return "Sending the file to the library…"
  return "Waiting to upload."
}

function wait(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms))
}
